import React, { useContext, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";

import { UserContext } from "../Contexts/UserContext/UserContext.js";
import ConfirmModal from "./ConfirmModal.js";


/**
 * LoginLogoutButton — reusable login/logout control.
 *
 * Renders "Login" (NavLink to /select_wallet) when no real wallet is loaded,
 * or "Logout" (opens a ConfirmModal, then navigates to /select_wallet) when a
 * real wallet is loaded.
 *
 * Props:
 *   - className: string (applied to the clickable element)
 *   - withIcon: boolean (prefixes the label with a FontAwesome icon)
 *   - onNavigate: () => void (optional — e.g. close the mobile menu; called
 *     on both login click and logout confirm)
 */
const LoginLogoutButton = ({
	className,
	withIcon = false,
	onNavigate,
}) => {
	const userContext = useContext(UserContext)
	const history = useHistory()
	const [showConfirm, setShowConfirm] = useState(false)

	const walletLoaded = !!(userContext.mnemonic && userContext.isMockWallet !== true)

	const handleConfirmLogout = async () => {
		setShowConfirm(false)
		// reset_wallet() clears mnemonic so the /select_wallet route guard
		// (in AppContent) doesn't bounce us back to /wallet. Without this,
		// navigating to /select_wallet with a loaded wallet would redirect
		// right back to /wallet and the logout would be a no-op.
		await userContext.reset_wallet()
		history.push("/select_wallet")
		if (onNavigate) onNavigate()
	}

	const handleLoginClick = () => {
		if (onNavigate) onNavigate()
	}

	const iconJsx = withIcon
		? <i className="fas fa-sign-out-alt" style={{ marginRight: "10px" }} />
		: null

	if (walletLoaded) {
		return (
			<>
				<span
					className={className}
					onClick={() => setShowConfirm(true)}
					style={{ cursor: "pointer" }}
				>
					{iconJsx}
					Logout
				</span>

				<ConfirmModal
					show={showConfirm}
					title="Logout"
					message={<>
						You are about to disconnect from this wallet.
						<br /><br />
						Make sure you have saved your seed phrase or backup file —
						you will need it to restore access to your wallet.
					</>}
					confirmText="Disconnect"
					onConfirm={handleConfirmLogout}
					onCancel={() => setShowConfirm(false)}
				/>
			</>
		)
	}

	return (
		<NavLink to="/select_wallet" className={className} onClick={handleLoginClick}>
			{iconJsx}
			Login
		</NavLink>
	)
}

export default LoginLogoutButton
